import { useState } from 'react';
import {
  Label,
  Input,
  Textarea,
  DateInput,
  Checkbox,
  RadioGroup,
  RadioGroupItem,
  FieldError,
  Button,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@hub/ui';
import { Lock, CheckCircle2 } from 'lucide-react';
import type { ViewPrimitive, BladeContext } from '../registry';
import { BladeLoading, BladeError, BladeDenied, forcedBladeState } from '../BladeStates';
import { useSubmitAction } from '../BladeActionSource';
import { useViewDefinition } from '../BladeDataSource';
import { ACTION_TRANSITION_STATUS } from '../actions';

// Form view-primitive (HUB-276/278) — a claim-coding form that exercises the @hub/ui
// form controls. As of HUB-407 it is WRITE-capable: submitting fires a real work-item
// status transition through the BladeActionSource seam (WorkQueueService.TransitionStatus),
// with pending/success/error/denied states, inline server-validation errors, and
// cache invalidation of the bound record/list so open blades refresh. The server
// validates the transition against the item lifecycle and emits a `status_changed`
// activity event, so the action enters the ledger (HUB-320) — no new event infra here.
//
// PHI: submitted values are never logged; only the work-item id crosses into the RPC
// and query keys. Content layer (lives in the blade body) — brand utilities + tokens.

/** The view key the form resolves its layout hints from (HUB-408). Overridable per
 *  blade via `params.view`; defaults to the seeded work-item form view. A resolved
 *  definition drives field labels (zero-regression fallback to the hardcoded labels
 *  when none is authored). */
const DEFAULT_FORM_VIEW = 'workitem_form';

/** Status transitions the form offers. Authoritative legality lives server-side in
 *  the item lifecycle (ComputePermittedActions is not yet a client-exposed RPC —
 *  HUB-407 follow-on), so we offer plausible next statuses and let the server accept
 *  or reject; a rejection renders inline against this field. */
const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'in_progress', label: 'In progress' },
  { value: 'on_hold', label: 'On hold' },
  { value: 'done', label: 'Done' },
];

function FieldRow({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-col gap-1.5">{children}</div>;
}

function ClaimForm({ ctx }: { ctx: BladeContext }) {
  const entityId = ctx.descriptor.entityId;
  const viewKey = ctx.descriptor.params?.view ?? DEFAULT_FORM_VIEW;
  const { data: viewDef } = useViewDefinition(viewKey);

  const [amount, setAmount] = useState('');
  const [status, setStatus] = useState('in_progress');
  const amountError = amount.trim() !== '' && Number.isNaN(Number(amount));

  const action = useSubmitAction({ kind: ACTION_TRANSITION_STATUS, entityId });
  const pending = action.status === 'pending';

  // HUB-408 consumption: when an active form view definition names the status field,
  // use its label; otherwise fall back to the hardcoded label (zero regression).
  const statusLabel =
    viewDef?.sections
      .flatMap((s) => s.fields)
      .find((f) => f.field === 'status')?.label ?? 'Status';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (amountError) return;
    action.submit({ status });
  };

  return (
    <form className="flex max-w-md flex-col gap-4" onSubmit={handleSubmit}>
      {action.status === 'success' && (
        <div
          role="status"
          className="flex items-center gap-2 rounded-coronis border border-coronis-teal-200 bg-coronis-teal-100 px-3 py-2 text-sm text-coronis-teal-700 dark:border-coronis-teal-700 dark:bg-coronis-teal-900/40 dark:text-coronis-teal-300"
        >
          <CheckCircle2 aria-hidden className="size-4 shrink-0" />
          Changes saved.
        </div>
      )}

      {action.status === 'denied' && (
        <div
          role="alert"
          className="flex items-center gap-2 rounded-coronis border border-border bg-muted px-3 py-2 text-sm text-muted-foreground"
        >
          <Lock aria-hidden className="size-4 shrink-0" />
          You don’t have permission to change this item.
        </div>
      )}

      {action.status === 'error' && action.formError && (
        <div
          role="alert"
          className="rounded-coronis border border-red-200 bg-red-100 px-3 py-2 text-sm text-red-700 dark:border-red-700 dark:bg-red-900/40 dark:text-red-300"
        >
          {action.formError}
        </div>
      )}

      <FieldRow>
        <Label htmlFor="payer">Payer</Label>
        <Select>
          <SelectTrigger id="payer">
            <SelectValue placeholder="Select payer" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="aetna">Aetna</SelectItem>
            <SelectItem value="bcbs">Blue Cross Blue Shield</SelectItem>
            <SelectItem value="uhc">UnitedHealthcare</SelectItem>
          </SelectContent>
        </Select>
      </FieldRow>

      <FieldRow>
        <Label htmlFor="dos">Date of service</Label>
        <DateInput id="dos" defaultValue="2026-05-12" />
      </FieldRow>

      <FieldRow>
        <Label htmlFor="amount">Billed amount</Label>
        <Input
          id="amount"
          inputMode="decimal"
          placeholder="0.00"
          value={amount}
          error={amountError}
          aria-describedby="amount-err"
          onChange={(e) => setAmount(e.target.value)}
        />
        <FieldError id="amount-err">{amountError ? 'Enter a valid dollar amount.' : ''}</FieldError>
      </FieldRow>

      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 text-sm font-medium text-foreground">{statusLabel}</legend>
        <RadioGroup name="status" value={status} onValueChange={setStatus}>
          {STATUS_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className="flex items-center gap-2 text-sm text-foreground"
            >
              <RadioGroupItem value={opt.value} /> {opt.label}
            </label>
          ))}
        </RadioGroup>
        <FieldError id="status-err">{action.fieldErrors.status ?? ''}</FieldError>
      </fieldset>

      <label className="flex items-center gap-2 text-sm text-foreground">
        <Checkbox defaultChecked /> Coding reviewed
      </label>

      <FieldRow>
        <Label htmlFor="notes">Notes</Label>
        <Textarea id="notes" placeholder="Add coding notes…" />
      </FieldRow>

      <div className="flex gap-2 pt-1">
        <Button type="submit" disabled={pending}>
          {pending ? 'Saving…' : 'Save'}
        </Button>
        <Button type="button" variant="outline" disabled={pending}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

export const formPrimitive: ViewPrimitive = {
  key: 'form',
  render: (ctx) => {
    const forced = forcedBladeState(ctx.descriptor);
    if (forced === 'loading') return <BladeLoading />;
    if (forced === 'error') return <BladeError />;
    if (forced === 'denied') return <BladeDenied description="This claim contains restricted PHI." />;
    return <ClaimForm ctx={ctx} />;
  },
};
